import { createEffect } from "solid-js";
import { orgSwitchEpoch } from "@/features/organizations/active-org";

/**
 * Multi-organization ISOLATION guard for open entity pointers.
 *
 * WMS routes deep-link the open entity in the URL (`?picking=`,
 * `?po=`, `?count=`, `?bom=`, `?return=`, `?product=`). That pointer is GLOBAL to
 * the route, but the entity belongs to ONE organization. The local PowerSync
 * cache contains data from ALL my orgs at once (`inventory_org`:
 * `organization_id IN my_org_ids`), so after switching companies in the
 * switcher that pointer would resolve —or the form would prefill— an entity from
 * the PREVIOUS organization (cross-company leak). On switching org we
 * discard it (same criterion as the active POS ticket guard in
 * comandero).
 *
 * Observes `orgSwitchEpoch` (which only increments on an EXPLICIT org switch), not
 * the scope directly: thus the scope settlement on boot
 * (visitor→real user, delayed org sync) does NOT wipe a legitimate deep-link
 * freshly opened.
 */
export function useResetOnOrgSwitch(reset: () => void): void {
  let first = true;
  createEffect(() => {
    orgSwitchEpoch(); // tracks explicit change epoch
    if (first) {
      first = false;
      return;
    }
    // The `reset` of several routes NAVIGATES (`?picking=`/`?po=`… → clean URL). If
    // run SYNCHRONOUSLY within the reactive flush of an org switch, that navigation
    // re-enters the same update while all WMS queries
    // resubscribe for the new `orgId` → cascade that FREEZES the tab (empirically
    // tested on /operations). We defer it outside the flush: the pointer is
    // discarded just the same (same microtask tick), without re-entrancy.
    queueMicrotask(reset);
  });
}
